import { useReducer, type Reducer } from "react";
import { unknown } from "zod";

type BaseData = { id: string };

type PayloadNested<SubType extends string, Data extends BaseData> = {
  subType: SubType;
  data: Data | Data[];
};

type PayloadFlat<Data extends BaseData> = Data | Data[];

type Payload<Data extends BaseData, SubType extends string = any> =
  | PayloadNested<SubType, Data>
  | PayloadFlat<Data>;

type CreateAction<
  SubType extends string,
  Data extends BaseData,
  P extends Payload<Data, SubType>
> = { type: "create"; payload: P };
type UpdateAction<
  SubType extends string,
  Data extends BaseData,
  P extends Payload<Data, SubType>
> = { type: "update"; payload: P };
type DeleteAction<
  SubType extends string,
  Data extends BaseData,
  P extends Payload<Data, SubType>
> = { type: "delete"; payload: P };
type ResetAction<
  SubType extends string,
  Data extends BaseData,
  P extends Payload<Data, SubType>
> = { type: "reset"; payload: Partial<P> };

export type Action<
  Data extends BaseData,
  P extends Payload<Data, SubType> = any,
  SubType extends string = any
> =
  | CreateAction<SubType, Data, P>
  | UpdateAction<SubType, Data, P>
  | DeleteAction<SubType, Data, P>
  | ResetAction<SubType, Data, P>;

type StateNested<SubType extends string, Data extends BaseData> = Record<SubType, Data[]>;
type StateFlat<Data extends BaseData> = Data;

type State<Data extends BaseData, SubType extends string = any> = StateNested<SubType, Data> | StateFlat<Data>;

// const crudReducer = <
//   Data extends BaseData,
//   S extends State<Data, SubType>,
//   P extends Payload<Data, SubType>,
//   SubType extends string = any,
// >(
//   prevState: S,
//   action: Action<Data, P, SubType>
// ): S => {
//   switch (action.type) {
//     case "create": {
//       const { payload } = action;
//       if (payload.subType) {
//         const { subType, data } = payload;
//         const newData = Array.isArray(data) ? data : [data];
//         return {
//           ...prevState,
//           [subType]: [...prevState[subType], ...newData],
//         };
//       }
//     }
//     case "update": {
//       const { payload } = action;
//       const { subType, data } = payload;
//       const newData = Array.isArray(data) ? data : [data];
//       const newDataMap = new Map(newData.map((val) => [val.id, val]));
//       return {
//         ...prevState,
//         [subType]: prevState[subType].map((val) =>
//           newDataMap.has(val.id) ? newDataMap.get(val.id) : val
//         ),
//       };
//     }
//     case "delete": {
//       const { payload } = action;
//       const { subType, data } = payload;
//       const newData = Array.isArray(data) ? data : [data];
//       const ids = new Set(newData.map((val) => val.id));
//       return {
//         ...prevState,
//         [subType]: prevState[subType].filter((val) => !ids.has(val.id)),
//       };
//     }
//     // case "reset": {
//     //   const { payload } = action;
//     //   const { subType, data } = payload;
//     //   if (data) {
//     //     const newData = Array.isArray(data) ? data : [data];
//     //     const ids = new Set(newData.map((entry) => entry.id));
//     //     if (subType) {
//     //       return {
//     //         ...prevState,
//     //         [subType]: prevState[subType].map((val) =>
//     //           ids.has(val.id) ? initialExpensesData[expenseType] : val
//     //         ),
//     //       };
//     //     } else {
//     //     }
//     //   } else {
//     //     return {
//     //       ...initialExpensesData,
//     //     };
//     //   }

//     // }
//     default: {
//       return prevState;
//     }
//   }
// };

const crudReducerNested = <
  SubType extends string,
  Data extends BaseData,
  S extends StateNested<SubType, Data>,
  P extends PayloadNested<SubType, Data>
>(
  prevState: S,
  action: Action<Data, P, SubType>
): S => {
  switch (action.type) {
    case "create": {
      const { payload } = action;
      const { subType, data } = payload;
      const newData = Array.isArray(data) ? data : [data];
      return {
        ...prevState,
        [subType]: [...prevState[subType], ...newData],
      };
    }
    case "update": {
      const { payload } = action;
      const { subType, data } = payload;
      const newData = Array.isArray(data) ? data : [data];
      const newDataMap = new Map(newData.map((val) => [val.id, val]));
      return {
        ...prevState,
        [subType]: prevState[subType].map((val) =>
          newDataMap.has(val.id) ? newDataMap.get(val.id) : val
        ),
      };
    }
    case "delete": {
      const { payload } = action;
      const { subType, data } = payload;
      const newData = Array.isArray(data) ? data : [data];
      const ids = new Set(newData.map((val) => val.id));
      return {
        ...prevState,
        [subType]: prevState[subType].filter((val) => !ids.has(val.id)),
      };
    }
    default: {
      return prevState;
    }
  }
};

const isArray = <T = any>(data: unknown): data is T[] =>
  Array.isArray(data);

const crudReducerFlat = <
  Data extends BaseData,
  S extends Data[],
  P extends PayloadFlat<Data>
>(
  prevState: S,
  action: Action<Data, P>
): S => {
  switch (action.type) {
    case "create": {
      const { payload } = action;
      const newData = isArray<Data>(payload) ? payload : [payload];
      return [
        ...prevState,
        ...newData,
      ] as S
    }
    case "update": {
      const { payload } = action;
      const newData = (Array.isArray(payload) ? payload : [payload]) as Data[];
      const newDataMap = new Map(newData.map((val) => [val.id, val]));
      return prevState.map((val) =>
        newDataMap.has(val.id) ? newDataMap.get(val.id)! : val
      ) as S;
    }
    case "delete": {
      const { payload } = action;
      const newData = (Array.isArray(payload) ? payload : [payload]) as Data[];
      const ids = new Set(newData.map((val) => val.id));
      const newState = prevState.filter((val) => !ids.has(val.id)) as S;
      return newState;
    }
    default: {
      return prevState;
    }
  }
};

type HandlerNested<SubType extends string, Data extends BaseData> = (
  subType: SubType,
  data: Data | Data[]
) => void;
export type CreateHandlerNested<
  SubType extends string,
  Data extends BaseData
> = HandlerNested<SubType, Data>;
export type UpdateHandlerNested<
  SubType extends string,
  Data extends BaseData
> = HandlerNested<SubType, Data>;
export type DeleteHandlerNested<
  SubType extends string,
  Data extends BaseData
> = HandlerNested<SubType, Data>;

const useCrudReducerNested = <SubType extends string, Data extends BaseData>(
  initialData: Record<SubType, Data[]>
) => {
  const [data, dispatcher] = useReducer(crudReducerNested, initialData);

  const handler =
    (type: Action<SubType, Data>["type"]): HandlerNested<SubType, Data> =>
    (subType, data) => {
      dispatcher({
        type,
        payload: {
          subType,
          data,
        },
      });
    };
  const handleCreate = handler("create");
  const handleUpdate = handler("update");
  const handleDelete = handler("delete");

  return {
    data,
    dispatcher,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
};

type HandlerFlat<Data extends BaseData> = (
  data: Data | Data[]
) => void;
export type CreateHandlerFlat<
  Data extends BaseData
> = HandlerFlat< Data>;
export type UpdateHandlerFlat<
  Data extends BaseData
> = HandlerFlat<Data>;
export type DeleteHandlerFlat<
  Data extends BaseData
> = HandlerFlat<Data>;

const useCrudReducerFlat = <Data extends BaseData>(
  initialData: Data[]
) => {
  const [data, dispatcher] = useReducer(crudReducerFlat, initialData);

  const handler =
    (type: Action<Data>["type"]): HandlerFlat<Data> =>
    (data) => {
      dispatcher({
        type,
        payload: data,
      });
    };
  const handleCreate = handler("create");
  const handleUpdate = handler("update");
  const handleDelete = handler("delete");

  return {
    data,
    dispatcher,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
};

export { useCrudReducerNested, useCrudReducerFlat };
